import { TrendingUp, TrendingDown, Info } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';
import { ImageWithFallback } from './figma/ImageWithFallback';

const kpiData = [
  {
    label: 'Awareness',
    value: '48%',
    delta: '+2.1 pts',
    isPositive: true,
    sparkline: [45, 46, 45, 47, 46, 48]
  },
  {
    label: 'Consideration',
    value: '31%',
    delta: '+1.4 pts',
    isPositive: true,
    sparkline: [28, 29, 30, 30, 31, 31]
  },
  {
    label: 'Preference',
    value: '18%',
    delta: '−0.8 pts',
    isPositive: false,
    sparkline: [19, 19, 20, 19, 19, 18]
  },
  {
    label: 'Category Fit',
    value: '42%',
    delta: '+3.0 pts',
    isPositive: true,
    sparkline: [38, 39, 40, 41, 42, 42]
  }
];

const brandMomentumData = [
  { month: 'Feb', merrell: 16, northFace: 22, salomon: 19, columbia: 14, keen: 12 },
  { month: 'Mar', merrell: 17, northFace: 22, salomon: 19, columbia: 14, keen: 12 },
  { month: 'Apr', merrell: 17, northFace: 23, salomon: 20, columbia: 14, keen: 13 },
  { month: 'May', merrell: 18, northFace: 23, salomon: 20, columbia: 15, keen: 13 },
  { month: 'Jun', merrell: 19, northFace: 24, salomon: 21, columbia: 15, keen: 14 },
  { month: 'Jul', merrell: 20, northFace: 25, salomon: 22, columbia: 16, keen: 14 },
  { month: 'Aug', merrell: 19, northFace: 25, salomon: 22, columbia: 15, keen: 14 },
  { month: 'Sep', merrell: 18, northFace: 24, salomon: 21, columbia: 15, keen: 13, annotation: 'Fall Hiking Campaign Launch' },
  { month: 'Oct', merrell: 19, northFace: 25, salomon: 21, columbia: 16, keen: 14 },
  { month: 'Nov', merrell: 20, northFace: 26, salomon: 22, columbia: 17, keen: 15, annotation: 'Holiday Sale Push' },
  { month: 'Dec', merrell: 19, northFace: 25, salomon: 22, columbia: 16, keen: 15 },
  { month: 'Jan', merrell: 18, northFace: 24, salomon: 21, columbia: 16, keen: 14 }
];

const competitorBenchmark = [
  { brand: 'The North Face', value: 24, isHighlighted: false },
  { brand: 'Salomon', value: 21, isHighlighted: false },
  { brand: 'Merrell', value: 18, isHighlighted: true },
  { brand: 'Columbia', value: 16, isHighlighted: false },
  { brand: 'KEEN', value: 14, isHighlighted: false }
];

const topAssociations = [
  'Waterproof', 'Gore-Tex', 'Trail running', 'Hiking boots',
  'Vibram sole', 'Lightweight', 'All-terrain', 'Comfort'
];

const productDrivers = [
  {
    id: 1,
    name: 'Moab 3 Hiking Boot',
    image: 'https://s7d4.scene7.com/is/image/WolverineWorldWide/MRLW-J036344-WHT-040722-S22-000?$dw-pdp-primary$',
    positive: 92,
    neutral: 6,
    negative: 2
  },
  {
    id: 2,
    name: 'Agility Peak 5',
    image: 'https://s7d4.scene7.com/is/image/WolverineWorldWide/MRLM-J068041-081722-F23-000?$dw-pdp-primary$',
    positive: 88,
    neutral: 9,
    negative: 3
  },
  {
    id: 3,
    name: 'Moab Speed 2',
    image: 'https://s7d4.scene7.com/is/image/WolverineWorldWide/MRLM-J038329-090424-F25-000?$dw-pdp-primary$',
    positive: 85,
    neutral: 11,
    negative: 4
  },
  {
    id: 4,
    name: 'ProMorph',
    image: 'https://s7d4.scene7.com/is/image/WolverineWorldWide/MRLM-J00005111-042125-S26-000?$dw-pdp-primary$',
    positive: 81,
    neutral: 15,
    negative: 4
  },
  {
    id: 5,
    name: 'Thermo Snow Grip',
    image: 'https://s7d4.scene7.com/is/image/WolverineWorldWide/MRLW-J038756-112524-F25-000?$dw-pdp-primary$',
    positive: 78,
    neutral: 17,
    negative: 5
  }
];

const mentionsTrendData = [
  { month: 'Aug', mentions: 2900 },
  { month: 'Sep', mentions: 3200 },
  { month: 'Oct', mentions: 3450 },
  { month: 'Nov', mentions: 3700 },
  { month: 'Dec', mentions: 3850 },
  { month: 'Jan', mentions: 3890 }
];

const sentimentMixData = [
  { name: 'Positive', value: 72, color: '#10b981' },
  { name: 'Neutral', value: 21, color: '#6b7280' },
  { name: 'Negative', value: 7, color: '#ef4444' }
];

const driversOfChange = {
  positive: [
    { label: 'Comfort', trend: 'up' },
    { label: 'Durability', trend: 'up' },
    { label: 'Waterproof', trend: 'up' }
  ],
  negative: [
    { label: 'Style', trend: 'down' },
    { label: 'Price sensitivity', trend: 'down' },
    { label: 'Stock availability', trend: 'down' }
  ]
};

export function BrandPulseBrandTracker() {
  return (
    <div className="space-y-6">
      {/* KPI Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        {kpiData.map((kpi, index) => (
          <Card key={index} className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="text-sm text-gray-500 mb-2">{kpi.label}</div>
              <div className="flex items-end justify-between mb-3">
                <span className="text-3xl text-gray-900">{kpi.value}</span>
                <span className={`text-sm flex items-center gap-1 ${kpi.isPositive ? 'text-green-600' : 'text-red-600'}`}>
                  {kpi.isPositive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                  {kpi.delta}
                </span>
              </div>
              {/* Mini Sparkline */}
              <div className="h-8">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={kpi.sparkline.map((v, _i) => ({ value: v }))}>
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke={kpi.isPositive ? '#10b981' : '#ef4444'}
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Main Section: Brand Momentum + Right Panel */}
      <div className="grid grid-cols-3 gap-6">
        {/* Left: Brand Momentum Chart (2 columns) */}
        <div className="col-span-2">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-gray-900">Brand Momentum Over Time</h3>
                <div className="flex gap-2">
                  <button className="px-3 py-1.5 text-xs rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200">
                    Awareness
                  </button>
                  <button className="px-3 py-1.5 text-xs rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200">
                    Consideration
                  </button>
                  <button className="px-3 py-1.5 text-xs rounded-full bg-purple-600 text-white">
                    Preference
                  </button>
                  <button className="px-3 py-1.5 text-xs rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200">
                    Category Fit
                  </button>
                </div>
              </div>

              {/* Chart */}
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={brandMomentumData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                    <XAxis
                      dataKey="month"
                      stroke="#9ca3af"
                      tick={{ fontSize: 12 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      stroke="#9ca3af"
                      tick={{ fontSize: 12 }}
                      axisLine={false}
                      tickLine={false}
                      domain={[0, 30]}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '12px'
                      }}
                    />
                    {/* Competitor Lines - Muted */}
                    <Line
                      key="northFace"
                      type="monotone"
                      dataKey="northFace"
                      stroke="#cbd5e1"
                      strokeWidth={2}
                      dot={false}
                      name="The North Face"
                    />
                    <Line
                      key="salomon"
                      type="monotone"
                      dataKey="salomon"
                      stroke="#d1d5db"
                      strokeWidth={2}
                      dot={false}
                      name="Salomon"
                    />
                    <Line
                      key="columbia"
                      type="monotone"
                      dataKey="columbia"
                      stroke="#e5e7eb"
                      strokeWidth={2}
                      dot={false}
                      name="Columbia"
                    />
                    <Line
                      key="keen"
                      type="monotone"
                      dataKey="keen"
                      stroke="#f3f4f6"
                      strokeWidth={2}
                      dot={false}
                      name="KEEN"
                    />
                    {/* Merrell Line - Highlighted */}
                    <Line
                      key="merrell"
                      type="monotone"
                      dataKey="merrell"
                      stroke="#be43ff"
                      strokeWidth={3}
                      dot={{ fill: '#be43ff', strokeWidth: 2, r: 4 }}
                      name="Merrell"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Chart Annotations */}
              <div className="flex items-center gap-4 mt-4 text-xs text-gray-500">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-purple-600"></div>
                  <span className="text-gray-900 font-medium">Merrell</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-gray-300"></div>
                  <span>The North Face</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-gray-300"></div>
                  <span>Salomon</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-gray-300"></div>
                  <span>Columbia</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-gray-300"></div>
                  <span>KEEN</span>
                </div>
              </div>

              {/* Annotations */}
              <div className="flex gap-4 mt-4">
                <div className="flex items-start gap-2 text-xs">
                  <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-medium text-gray-900">Sep:</span>{' '}
                    <span className="text-gray-600">Fall Hiking Campaign Launch</span>
                  </div>
                </div>
                <div className="flex items-start gap-2 text-xs">
                  <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-medium text-gray-900">Nov:</span>{' '}
                    <span className="text-gray-600">Holiday Sale Push</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Panel Stack */}
        <div className="space-y-4">
          {/* Competitor Benchmark */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <h3 className="text-sm text-gray-900 mb-4 font-medium">
                Competitor Benchmark — Preference (Jan)
              </h3>
              <div className="space-y-3">
                {competitorBenchmark.map((item, index) => (
                  <div
                    key={index}
                    className={`flex items-center justify-between p-2 rounded-lg ${
                      item.isHighlighted ? 'bg-purple-50 border border-purple-200' : ''
                    }`}
                  >
                    <span className={`text-sm ${item.isHighlighted ? 'text-gray-900 font-medium' : 'text-gray-700'}`}>
                      {item.brand}
                    </span>
                    <span className={`text-sm ${item.isHighlighted ? 'text-purple-600 font-bold' : 'text-gray-900 font-medium'}`}>
                      {item.value}%
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Perception Drivers */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <h3 className="text-sm text-gray-900 mb-4 font-medium">Top Associations</h3>
              <div className="flex flex-wrap gap-2">
                {topAssociations.map((keyword, index) => (
                  <Badge
                    key={index}
                    className="bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100"
                  >
                    {keyword}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Biggest Shift */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <h3 className="text-sm text-gray-900 mb-3 font-medium">Biggest Shift</h3>
              <p className="text-sm text-gray-900 mb-2">
                Preference declined <span className="text-red-600 font-medium">−0.8 pts</span> in the last 30 days.
              </p>
              <p className="text-xs text-gray-600 mb-4">
                Driven by lower 'Stylish' mentions and higher price sensitivity.
              </p>
              <Button className="w-full bg-purple-600 text-white hover:bg-purple-700 text-sm h-9">
                View Details
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Product Drivers */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-6">
          <h3 className="text-gray-900 mb-6">Product Drivers (Sentiment by Product)</h3>
          <div className="space-y-6">
            {productDrivers.map((product) => (
              <div key={product.id} className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                  <ImageWithFallback
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1">
                  <div className="text-sm text-gray-900 mb-2 font-medium">{product.name}</div>
                  <div className="flex items-center gap-1 h-6">
                    {/* Positive */}
                    <div
                      className="h-full bg-green-500 rounded-l-md flex items-center justify-center text-xs text-white font-medium"
                      style={{ width: `${product.positive}%` }}
                    >
                      {product.positive >= 20 && `${product.positive}%`}
                    </div>
                    {/* Neutral */}
                    <div
                      className="h-full bg-gray-400 flex items-center justify-center text-xs text-white font-medium"
                      style={{ width: `${product.neutral}%` }}
                    >
                      {product.neutral >= 10 && `${product.neutral}%`}
                    </div>
                    {/* Negative */}
                    <div
                      className="h-full bg-red-500 rounded-r-md flex items-center justify-center text-xs text-white font-medium"
                      style={{ width: `${product.negative}%` }}
                    >
                      {product.negative >= 5 && `${product.negative}%`}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Brand Tracker Insights */}
      <div>
        <div className="mb-4">
          <h2 className="text-gray-900 mb-1">Brand Tracker Insights</h2>
          <p className="text-sm text-gray-500">
            Signals from real-world conversations and reviews that impact brand momentum.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-6">
          {/* Mentions Trend */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="text-sm text-gray-500 mb-1">Monthly Mentions</div>
              <div className="text-3xl text-gray-900 mb-1">3,890</div>
              <div className="text-sm text-green-600 mb-4 flex items-center gap-1">
                <TrendingUp className="w-4 h-4" />
                +690 from last month
              </div>
              <div className="h-24">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={mentionsTrendData}>
                    <Bar dataKey="mentions" fill="#be43ff" radius={[4, 4, 0, 0]} />
                    <XAxis dataKey="month" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Sentiment Mix */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <h3 className="text-sm text-gray-900 mb-4 font-medium">Sentiment Mix</h3>
              <div className="h-40 flex items-center justify-center mb-4">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={sentimentMixData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={60}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {sentimentMixData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex items-center justify-center gap-4 text-xs">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  <span className="text-gray-600">72%</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                  <span className="text-gray-600">21%</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-red-500"></div>
                  <span className="text-gray-600">7%</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Drivers of Change */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <h3 className="text-sm text-gray-900 mb-4 font-medium">What's Driving Change</h3>
              <div className="space-y-4">
                <div>
                  <div className="text-xs text-gray-500 mb-2 font-medium">Top Positive Drivers</div>
                  <div className="space-y-1.5">
                    {driversOfChange.positive.map((driver, index) => (
                      <div key={index} className="flex items-center justify-between text-sm">
                        <span className="text-gray-700">{driver.label}</span>
                        <TrendingUp className="w-4 h-4 text-green-600" />
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-2 font-medium">Top Negative Drivers</div>
                  <div className="space-y-1.5">
                    {driversOfChange.negative.map((driver, index) => (
                      <div key={index} className="flex items-center justify-between text-sm">
                        <span className="text-gray-700">{driver.label}</span>
                        <TrendingDown className="w-4 h-4 text-red-600" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <button className="text-purple-600 hover:text-purple-700 text-sm font-medium mt-4">
                See full breakdown →
              </button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}